import { useEffect, useMemo, useRef, useState } from "react";

import { Box, CircularProgress, Link, Typography } from "@mui/material";
import { GetSubmissionResponsePortal } from "@rubi/api-generated";
import { GoogleMapsProvider, useMapColorScheme } from "@rubi/ui";
import { colors } from "@rubi/ui";
import {
  AdvancedMarker,
  Map,
  Pin,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";

import { ICoordinates, isValidCoordinate, PlaceRef } from "./submissionFields";

// Same vector map id used by ReportsMap; required for AdvancedMarker.
const GOOGLE_MAP_ID = "4380cd85df35e961";

export interface MatchMapEntry {
  key: string;
  submission: GetSubmissionResponsePortal | null;
  attachmentUrl: string | null;
  attachmentFilename: string | null;
  matchNotes: string | null;
  matchedOn: string | null;
  isSource: boolean;
  /** Valid stored geometry, or null when it must be geocoded from placeRef. */
  coords: ICoordinates | null;
  /** place_id/address to geocode when `coords` is null. */
  placeRef: PlaceRef | null;
  crestUrl: string | null;
  agencyName: string;
}

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

interface CrestMarkerProps {
  entry: MatchMapEntry;
  coords: ICoordinates;
  selected: boolean;
  onSelect: (entry: MatchMapEntry) => void;
}

const CrestMarker = ({
  entry,
  coords,
  selected,
  onSelect,
}: CrestMarkerProps) => {
  const size = entry.isSource ? 52 : 40;
  const ringColor = entry.isSource
    ? colors.primary.main
    : colors.primary.contrastText;

  return (
    <AdvancedMarker
      position={coords}
      onClick={() => onSelect(entry)}
      clickable
      title={entry.agencyName}
      zIndex={selected ? 1000 : entry.isSource ? 500 : 0}
    >
      {entry.crestUrl ? (
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            backgroundColor: colors.primary.contrastText,
            border: `3px solid ${selected ? colors.error.main : ringColor}`,
            boxShadow: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            transform: selected ? "scale(1.15)" : "none",
            transition: "transform 120ms ease",
          }}
        >
          <Box
            component="img"
            src={entry.crestUrl}
            alt={entry.agencyName}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              p: 0.5,
            }}
          />
        </Box>
      ) : (
        <Pin
          scale={selected ? 1.4 : entry.isSource ? 1.2 : 1}
          background={entry.isSource ? colors.primary.main : undefined}
          borderColor={selected ? colors.error.main : undefined}
          glyph={initialsOf(entry.agencyName)}
          glyphColor={colors.primary.contrastText}
        />
      )}
    </AdvancedMarker>
  );
};

// Zoom out (or in) so every located pin fits in view. Re-runs whenever the set
// of located coordinates changes or the map finishes loading.
const FitBounds = ({
  coords,
  enabled,
}: {
  coords: ICoordinates[];
  enabled: boolean;
}) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !enabled || coords.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    coords.forEach((c) => bounds.extend(c));
    map.fitBounds(bounds, { top: 80, right: 80, bottom: 80, left: 80 });
    // A single pin (or a very tight cluster) fits at an extreme zoom; cap it.
    const zoom = map.getZoom();
    if (zoom !== undefined && zoom > 15) map.setZoom(15);
  }, [map, enabled, coords]);

  return null;
};

interface ImageMatchMapProps {
  entries: MatchMapEntry[];
  selectedKey: string | null;
  onSelect: (entry: MatchMapEntry) => void;
}

const ImageMatchMapContent = ({
  entries,
  selectedKey,
  onSelect,
}: ImageMatchMapProps) => {
  const mapColorScheme = useMapColorScheme();
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  // Coordinates recovered by geocoding a placeRef, keyed by entry.
  const [geocoded, setGeocoded] = useState<Record<string, ICoordinates>>({});
  const [resolving, setResolving] = useState(0);
  const requested = useRef<Set<string>>(new Set());
  const geocodingLib = useMapsLibrary("geocoding");

  // Geocode any entry whose stored geometry was missing/invalid but which has a
  // place_id/address. The stored geometry for some submissions is (0, 0).
  useEffect(() => {
    if (!geocodingLib) return;
    const geocoder = new geocodingLib.Geocoder();

    entries.forEach((entry) => {
      if (entry.coords || !entry.placeRef) return;
      const { placeId, address } = entry.placeRef;
      if (!placeId && !address) return;
      if (requested.current.has(entry.key)) return;
      requested.current.add(entry.key);

      const request: google.maps.GeocoderRequest = placeId
        ? { placeId }
        : { address: address as string };

      setResolving((n) => n + 1);
      geocoder.geocode(request, (results, status) => {
        setResolving((n) => n - 1);
        if (status === "OK" && results && results[0]) {
          const loc = results[0].geometry.location;
          const lat = loc.lat();
          const lng = loc.lng();
          if (isValidCoordinate(lat, lng)) {
            setGeocoded((prev) => ({ ...prev, [entry.key]: { lat, lng } }));
          }
        }
      });
    });
  }, [geocodingLib, entries]);

  const located = useMemo(
    () =>
      entries
        .map((entry) => ({
          entry,
          coords: entry.coords ?? geocoded[entry.key] ?? null,
        }))
        .filter(
          (x): x is { entry: MatchMapEntry; coords: ICoordinates } =>
            x.coords !== null,
        ),
    [entries, geocoded],
  );

  const unlocated = useMemo(
    () => entries.filter((e) => !(e.coords ?? geocoded[e.key])),
    [entries, geocoded],
  );

  const locatedCoords = useMemo(() => located.map((l) => l.coords), [located]);

  const center = useMemo(() => {
    if (locatedCoords.length === 0) return null;
    const sum = locatedCoords.reduce(
      (acc, c) => ({ lat: acc.lat + c.lat, lng: acc.lng + c.lng }),
      { lat: 0, lng: 0 },
    );
    return {
      lat: sum.lat / locatedCoords.length,
      lng: sum.lng / locatedCoords.length,
    };
  }, [locatedCoords]);

  // Whether we're still waiting on geocoding before anything can be plotted.
  const stillLocating =
    located.length === 0 &&
    (resolving > 0 ||
      (!geocodingLib && entries.some((e) => !e.coords && e.placeRef)));

  return (
    <Box>
      {center ? (
        <Box
          sx={{
            height: "70vh",
            borderRadius: 1,
            overflow: "hidden",
            border: "1px solid var(--divider)",
          }}
        >
          <Map
            colorScheme={mapColorScheme}
            defaultCenter={center}
            defaultZoom={locatedCoords.length === 1 ? 13 : 10}
            mapId={GOOGLE_MAP_ID}
            gestureHandling="greedy"
            onTilesLoaded={() => setIsMapLoaded(true)}
          >
            <FitBounds coords={locatedCoords} enabled={isMapLoaded} />
            {isMapLoaded &&
              located.map(({ entry, coords }) => (
                <CrestMarker
                  key={entry.key}
                  entry={entry}
                  coords={coords}
                  selected={entry.key === selectedKey}
                  onSelect={onSelect}
                />
              ))}
          </Map>
        </Box>
      ) : (
        <Box
          sx={{
            height: "30vh",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            alignItems: "center",
            justifyContent: "center",
            border: "1px dashed var(--divider)",
            borderRadius: 1,
          }}
        >
          {stillLocating && <CircularProgress size={24} />}
          <Typography variant="body2" color="text.secondary">
            {stillLocating
              ? "Locating addresses…"
              : "None of the submissions in this report have a mappable location."}
          </Typography>
        </Box>
      )}

      {unlocated.length > 0 && !stillLocating && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {unlocated.length} submission
            {unlocated.length === 1 ? "" : "s"} without a mapped location:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {unlocated.map((entry) => (
              <Link
                key={entry.key}
                component="button"
                variant="body2"
                underline="hover"
                onClick={() => onSelect(entry)}
                sx={{ textAlign: "left" }}
              >
                {entry.isSource ? "Source — " : ""}
                {entry.submission?.tracking_number ||
                  entry.submission?.incident_number ||
                  entry.agencyName ||
                  "Submission"}
              </Link>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const ImageMatchMap = (props: ImageMatchMapProps) => (
  <GoogleMapsProvider>
    <ImageMatchMapContent {...props} />
  </GoogleMapsProvider>
);

export default ImageMatchMap;
